import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminHeader from '../../components/navigation/AdminHeader';
import AdminNavigation from '../../components/navigation/AdminNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';
import FilterSidebar from './components/FilterSidebar';
import RequestsGrid from './components/RequestsGrid';
import DetailPanel from './components/DetailPanel';
import BulkOperationsToolbar from './components/BulkOperationsToolbar';

const ServiceRequestManagementDashboard = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    urgency: 'all',
    issueType: 'all',
    dateRange: 'all',
    technician: 'all'
  });
  const [showFilters, setShowFilters] = useState(true);

  useEffect(() => {
    fetchServiceRequests();

    // Set up real-time subscription
    const subscription = supabase
      ?.channel('service_requests_changes')
      ?.on('postgres_changes', 
        { event: '*', schema: 'public', table: 'service_requests' },
        (payload) => {
          console.log('Real-time update:', payload);
          fetchServiceRequests();
        }
      )
      ?.subscribe();

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  useEffect(() => {
    applyFilters();
  }, [requests, filters]);

  const fetchServiceRequests = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        ?.from('service_requests')
        ?.select('*')
        ?.order('created_at', { ascending: false });

      if (fetchError) {
        console.error('Fetch error:', fetchError);
        setError('Failed to load service requests.');
        return;
      }

      setRequests(data || []);
    } catch (err) {
      console.error('Error:', err);
      setError('An unexpected error occurred.');
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...requests];

    // Search filter
    if (filters?.search?.trim()) {
      const query = filters?.search?.toLowerCase();
      filtered = filtered?.filter(req =>
        req?.request_number?.toLowerCase()?.includes(query) ||
        req?.driver_phone?.toLowerCase()?.includes(query) ||
        req?.unit_number?.toLowerCase()?.includes(query) ||
        req?.issue_type?.toLowerCase()?.includes(query)
      );
    }

    // Status filter
    if (filters?.status !== 'all') {
      filtered = filtered?.filter(req => req?.status === filters?.status);
    }

    // Urgency filter
    if (filters?.urgency !== 'all') {
      filtered = filtered?.filter(req => req?.urgency === filters?.urgency);
    }

    // Issue type filter
    if (filters?.issueType !== 'all') {
      filtered = filtered?.filter(req => req?.issue_type === filters?.issueType);
    }

    // Date range filter
    if (filters?.dateRange !== 'all') {
      const now = new Date();
      const filterDate = new Date();

      switch (filters?.dateRange) {
        case 'today':
          filterDate?.setHours(0, 0, 0, 0);
          filtered = filtered?.filter(req => new Date(req?.created_at) >= filterDate);
          break;
        case 'week':
          filterDate?.setDate(now?.getDate() - 7);
          filtered = filtered?.filter(req => new Date(req?.created_at) >= filterDate);
          break;
        case 'month':
          filterDate?.setDate(now?.getDate() - 30);
          filtered = filtered?.filter(req => new Date(req?.created_at) >= filterDate);
          break;
      }
    }

    setFilteredRequests(filtered);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      status: 'all',
      urgency: 'all',
      issueType: 'all',
      dateRange: 'all',
      technician: 'all'
    });
  };

  const handleSelectRequest = (requestId, checked) => {
    if (checked) {
      setSelectedRequests(prev => [...prev, requestId]);
    } else {
      setSelectedRequests(prev => prev?.filter(id => id !== requestId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedRequests(filteredRequests?.map(req => req?.id));
    } else {
      setSelectedRequests([]);
    }
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setShowDetailPanel(true);
  };

  const handleUpdateStatus = async (requestId, newStatus) => {
    try {
      const { error: updateError } = await supabase
        ?.from('service_requests')
        ?.update({ status: newStatus })
        ?.eq('id', requestId);

      if (updateError) {
        console.error('Update error:', updateError);
        alert('Failed to update status');
        return;
      }

      fetchServiceRequests();
    } catch (err) {
      console.error('Error:', err);
      alert('An unexpected error occurred');
    }
  };

  const handleBulkStatusUpdate = async (status) => {
    if (selectedRequests?.length === 0) {
      alert('Please select requests to update');
      return;
    }

    try {
      const { error: updateError } = await supabase
        ?.from('service_requests')
        ?.update({ status })
        ?.in('id', selectedRequests);

      if (updateError) {
        console.error('Bulk update error:', updateError);
        alert('Failed to update requests');
        return;
      }

      setSelectedRequests([]);
      fetchServiceRequests();
    } catch (err) {
      console.error('Error:', err);
      alert('An unexpected error occurred');
    }
  };

  const handleExport = () => {
    // Generate CSV export
    const headers = ['Request ID', 'Driver Phone', 'Unit Number', 'Urgency', 'Issue Type', 'Status', 'Created At'];
    const csvData = filteredRequests?.map(req => [
      req?.request_number,
      req?.driver_phone,
      req?.unit_number,
      req?.urgency,
      req?.issue_type,
      req?.status,
      new Date(req?.created_at)?.toLocaleString()
    ]);

    const csvContent = [
      headers?.join(','),
      ...csvData?.map(row => row?.join(','))
    ]?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window?.URL?.createObjectURL(blob);
    const a = document?.createElement('a');
    a.href = url;
    a.download = `service-requests-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    a?.click();
  };

  return (
    <div className="min-h-screen bg-background">
      <AdminHeader />
      <AdminNavigation />

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 mt-[7.5rem]">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Service Request Management</h1>
            <p className="text-sm text-muted-foreground mt-1">
              View, filter, prioritize, and respond to driver service requests
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              size="sm"
              iconName="Download"
              onClick={handleExport}
            >
              Export
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName={showFilters ? 'ChevronLeft' : 'ChevronRight'}
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? 'Hide' : 'Show'} Filters
            </Button>
            <Button
              variant="default"
              size="sm"
              iconName="RefreshCw"
              onClick={fetchServiceRequests}
            >
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Total Requests</p>
                <p className="text-2xl font-bold text-foreground mt-1">{requests?.length}</p>
              </div>
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="FileText" size={20} color="var(--color-primary)" strokeWidth={2} />
              </div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Pending</p>
                <p className="text-2xl font-bold text-warning mt-1">
                  {requests?.filter(r => r?.status === 'pending')?.length}
                </p>
              </div>
              <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
                <Icon name="Clock" size={20} color="var(--color-warning)" strokeWidth={2} />
              </div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase">In Progress</p>
                <p className="text-2xl font-bold text-primary mt-1">
                  {requests?.filter(r => r?.status === 'in-progress')?.length}
                </p>
              </div>
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Activity" size={20} color="var(--color-primary)" strokeWidth={2} />
              </div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Emergency</p>
                <p className="text-2xl font-bold text-destructive mt-1">
                  {requests?.filter(r => r?.urgency === 'Emergency')?.length}
                </p>
              </div>
              <div className="w-10 h-10 bg-destructive/10 rounded-lg flex items-center justify-center">
                <Icon name="AlertTriangle" size={20} color="var(--color-destructive)" strokeWidth={2} />
              </div>
            </div>
          </div>
        </div>

        {/* Bulk Operations Toolbar */}
        {selectedRequests?.length > 0 && (
          <BulkOperationsToolbar
            selectedCount={selectedRequests?.length}
            onBulkStatusUpdate={handleBulkStatusUpdate}
            onClearSelection={() => setSelectedRequests([])}
          />
        )}

        {/* Main Content */}
        <div className="grid grid-cols-12 gap-6">
          {/* Filter Sidebar */}
          {showFilters && (
            <div className="col-span-12 lg:col-span-2">
              <FilterSidebar
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
              />
            </div>
          )}

          {/* Requests Grid */}
          <div className={showFilters ? 'col-span-12 lg:col-span-10' : 'col-span-12'}>
            {isLoading ? (
              <div className="bg-card border border-border rounded-lg p-12 text-center">
                <Icon name="Loader2" size={48} className="animate-spin mx-auto text-primary mb-4" />
                <p className="text-muted-foreground">Loading service requests...</p>
              </div>
            ) : error ? (
              <div className="bg-card border border-destructive rounded-lg p-12 text-center">
                <Icon name="AlertCircle" size={48} className="mx-auto text-destructive mb-4" />
                <p className="text-destructive font-medium">{error}</p>
                <Button variant="outline" size="sm" className="mt-4" onClick={fetchServiceRequests}>
                  Retry
                </Button>
              </div>
            ) : (
              <RequestsGrid
                requests={filteredRequests}
                selectedRequests={selectedRequests}
                onSelectRequest={handleSelectRequest}
                onSelectAll={handleSelectAll}
                onViewDetails={handleViewDetails}
                onUpdateStatus={handleUpdateStatus}
              />
            )}
          </div>
        </div>
      </main>

      {/* Detail Modal — rendered outside the grid so it overlays the full page */}
      {showDetailPanel && selectedRequest && (
        <DetailPanel
          request={selectedRequest}
          onClose={() => { setShowDetailPanel(false); setSelectedRequest(null); }}
          onUpdateStatus={handleUpdateStatus}
        />
      )}
    </div>
  );
};

export default ServiceRequestManagementDashboard;